import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Search, RefreshCw, XCircle, ChevronRight, Clock, Loader2, AlertCircle } from 'lucide-react';
import { FullPagination } from '../../components/admin/FullPagination';
import { difformityService, DifformityListResponse } from '../../services/difformityService';

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  nuova: { label: 'Nuova', color: 'bg-red-100 text-red-700' },
  in_gestione: { label: 'In Gestione', color: 'bg-amber-100 text-amber-700' },
  chiusa: { label: 'Chiusa', color: 'bg-emerald-100 text-emerald-700' },
};

const PAGE_SIZE = 25;

function fmtDateTime(iso: string) {
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso?.slice(0, 16) ?? '—'; }
}

export default function AdminDifformityPage() {
  const [data, setData] = useState<DifformityListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await difformityService.adminList(params);
      setData(res);
    } catch {
      setData({ items: [], total: 0, page: 1, limit: PAGE_SIZE, pages: 1, hasNext: false, hasPrev: false });
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="admin-difformity-page">
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-[#2B5EA7]" />
              Segnalazioni Difformità
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Difformità sui prodotti segnalate dai clienti</p>
          </div>
          <button
            onClick={fetchData}
            className="h-9 w-9 rounded-xl border flex items-center justify-center hover:bg-gray-50 transition"
            title="Aggiorna"
            data-testid="difformity-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* KPI */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Totale', val: data.total, color: 'text-[#2B5EA7]' },
              { label: 'Nuove', val: data.items.filter(r => r.status === 'nuova').length, color: 'text-red-600' },
              { label: 'In gestione', val: data.items.filter(r => r.status === 'in_gestione').length, color: 'text-amber-600' },
              { label: 'Chiuse', val: data.items.filter(r => r.status === 'chiusa').length, color: 'text-emerald-600' },
            ].map(k => (
              <div key={k.label} className="bg-white rounded-xl border px-4 py-3">
                <div className={`text-2xl font-bold ${k.color}`}>{k.val}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white rounded-xl border p-3 sm:p-4 mb-4 flex flex-wrap gap-2 sm:gap-3 items-center">
          <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }} className="flex gap-2 flex-1 min-w-[220px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="ID, SKU, prodotto o cliente…"
                className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B5EA7]/30"
                data-testid="difformity-search-input"
              />
            </div>
            <button type="submit" className="h-9 px-4 rounded-lg bg-[#2B5EA7] text-white text-sm hover:bg-[#1E4D8C] transition">Cerca</button>
          </form>
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="h-9 text-sm border rounded-lg px-3 bg-white focus:outline-none"
            data-testid="difformity-status-filter"
          >
            <option value="">Tutti gli stati</option>
            {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {(search || status) && (
            <button
              onClick={() => { setSearch(''); setSearchInput(''); setStatus(''); setPage(1); }}
              className="h-9 px-3 rounded-lg border text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              data-testid="difformity-clear-filters"
            >
              <XCircle className="w-3.5 h-3.5" />Azzera
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-[#2B5EA7] animate-spin" />
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="difformity-empty-state">
              <AlertCircle className="w-10 h-10 mb-3 opacity-30" />
              <p className="font-medium">Nessuna segnalazione trovata.</p>
              <p className="text-sm mt-1 text-center max-w-sm">Le difformità segnalate dai clienti sulle pagine prodotto appariranno qui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">#</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Cliente</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Prodotto</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide hidden md:table-cell">Data</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Stato</th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(r => {
                    const st = STATUS_MAP[r.status] ?? STATUS_MAP.nuova;
                    return (
                      <tr
                        key={r.id}
                        className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors"
                        data-testid={`difformity-row-${r.id}`}
                      >
                        <td className="px-4 py-3 text-sm font-mono font-semibold text-[#2B5EA7] whitespace-nowrap">{r.id}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium">{r.clientCompany || '—'}</div>
                          {r.clientEmail && <div className="text-[11px] text-muted-foreground hidden sm:block">{r.clientEmail}</div>}
                          <div className="flex items-center gap-2 mt-1 lg:hidden text-[10px] text-muted-foreground">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                            <span className="md:hidden">·</span>
                            <span className="md:hidden">{fmtDateTime(r.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-mono text-[hsl(214,58%,41%)]">{r.sku}</div>
                          <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.productTitle}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap hidden md:table-cell">
                          <Clock className="w-3 h-3 inline-block mr-1 -mt-0.5" />{fmtDateTime(r.createdAt)}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/admin/difformita/${r.id}`}
                            className="inline-flex items-center gap-1 text-[11px] text-[#2B5EA7] hover:underline font-medium whitespace-nowrap"
                            data-testid={`difformity-detail-link-${r.id}`}
                          >
                            Dettagli <ChevronRight className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data && (
          <FullPagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            pageSize={data.limit}
            onPageChange={setPage}
            testIdPrefix="difformity-pagination"
          />
        )}
      </div>
    </div>
  );
}
